import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Camera, FileSpreadsheet, History, Table, Trash2 } from 'lucide-react'
import { Button } from '~/components/ui/button'
import { ErrorBanner } from '~/components/ui/error-banner'
import { Modal } from '~/components/ui/modal'
import { deleteIngestBatch, listIngestBatches } from '~/api/monitoring'
import type { IngestBatch } from '~/api/types'
import { formatDateTime } from '~/lib/date-utils'
import { errMessage } from '~/lib/errors'

/** 배치 표시명 — 파일명이 없으면 종류별 대체 라벨. */
function batchLabel(b: IngestBatch): string {
    if (b.filename) return b.filename
    return b.kind === 'image'
        ? `이미지 ${b.rowCount}행`
        : `업로드 ${b.rowCount}행`
}

/**
 * 적재 이력 — 지금까지 올린 CSV·엑셀/이미지 업로드를 최신순으로 보여주고,
 * 잘못 올린 업로드를 삭제(그 업로드로 적재된 측정값도 함께 제거)한다.
 */
export function IngestHistory({
    dashboardId,
    refreshKey = 0,
    onDeleted,
}: {
    dashboardId: string
    /** 값이 바뀌면 목록을 다시 불러온다(업로드 성공 후 갱신용). */
    refreshKey?: number
    /** 삭제 후 상위에서 측정값·보드 뷰를 다시 불러오도록 알린다. */
    onDeleted?: () => void
}) {
    const navigate = useNavigate()
    const [batches, setBatches] = useState<IngestBatch[]>([])
    const [loading, setLoading] = useState(true)
    const [err, setErr] = useState<string | null>(null)
    const [confirmBatch, setConfirmBatch] = useState<IngestBatch | null>(null)
    const [deleting, setDeleting] = useState(false)

    const load = useCallback(() => {
        setLoading(true)
        listIngestBatches(dashboardId)
            .then((list) => {
                setBatches(list)
                setErr(null)
            })
            .catch((e: unknown) =>
                setErr(errMessage(e, '적재 이력을 불러오지 못했어요.'))
            )
            .finally(() => setLoading(false))
    }, [dashboardId])

    useEffect(() => {
        load()
    }, [load, refreshKey])

    const onConfirmDelete = async () => {
        if (!confirmBatch) return
        setDeleting(true)
        setErr(null)
        try {
            await deleteIngestBatch(dashboardId, confirmBatch.id)
            setConfirmBatch(null)
            load()
            onDeleted?.()
        } catch (e: unknown) {
            setErr(errMessage(e, '삭제에 실패했어요.'))
        } finally {
            setDeleting(false)
        }
    }

    return (
        <section className='border-border bg-card rounded-md border p-5'>
            <div className='mb-3 flex items-start gap-3'>
                <div className='bg-accent text-primary grid size-10 shrink-0 place-items-center rounded-md'>
                    <History className='size-5' />
                </div>
                <div>
                    <h3 className='text-foreground text-sm font-semibold'>
                        적재 이력
                    </h3>
                    <p className='text-muted-foreground mt-0.5 text-xs'>
                        지금까지 올린 파일·사진이에요. 잘못 올렸다면 삭제하면
                        그 업로드로 적재된 측정값도 함께 제거됩니다.
                    </p>
                </div>
            </div>

            {loading ? (
                <p className='text-muted-foreground text-sm'>불러오는 중…</p>
            ) : batches.length === 0 ? (
                <p className='text-muted-foreground text-sm'>
                    아직 적재 이력이 없어요.
                </p>
            ) : (
                <ul className='divide-border divide-y'>
                    {batches.map((b) => (
                        <li
                            key={b.id}
                            className='flex items-center gap-3 py-2.5'>
                            <div className='bg-muted text-muted-foreground grid size-8 shrink-0 place-items-center rounded-md'>
                                {b.kind === 'image' ? (
                                    <Camera className='size-4' />
                                ) : (
                                    <FileSpreadsheet className='size-4' />
                                )}
                            </div>
                            <div className='min-w-0 flex-1'>
                                <p className='text-foreground truncate text-sm font-medium'>
                                    {batchLabel(b)}
                                </p>
                                <p className='text-muted-foreground text-xs'>
                                    {b.rowCount.toLocaleString()}건 적재 ·{' '}
                                    {formatDateTime(b.createdAt)}
                                </p>
                            </div>
                            {b.rowCount > 0 ? (
                                <Button
                                    size='sm'
                                    variant='outline'
                                    onClick={() =>
                                        navigate(
                                            `/monitoring/${dashboardId}/database?batch=${b.id}`
                                        )
                                    }>
                                    <Table />
                                    측정값 보기
                                </Button>
                            ) : null}
                            <Button
                                size='sm'
                                variant='outline'
                                onClick={() => setConfirmBatch(b)}>
                                <Trash2 />
                                삭제
                            </Button>
                        </li>
                    ))}
                </ul>
            )}

            {err ? (
                <ErrorBanner className='bg-point-subtle text-point mt-3'>
                    {err}
                </ErrorBanner>
            ) : null}

            <Modal
                open={confirmBatch != null}
                onClose={() => (deleting ? undefined : setConfirmBatch(null))}
                title='적재 이력 삭제'>
                {confirmBatch ? (
                    <div className='grid gap-4'>
                        <div>
                            <p className='text-foreground text-sm'>
                                <span className='font-medium'>
                                    {batchLabel(confirmBatch)}
                                </span>{' '}
                                업로드를 삭제할까요?
                            </p>
                            <p className='text-point mt-2 text-sm'>
                                이 업로드로 적재된 측정값{' '}
                                {confirmBatch.rowCount.toLocaleString()}건도 함께
                                삭제됩니다. 되돌릴 수 없어요.
                            </p>
                        </div>
                        <div className='flex justify-end gap-2'>
                            <Button
                                variant='outline'
                                disabled={deleting}
                                onClick={() => setConfirmBatch(null)}>
                                취소
                            </Button>
                            <Button
                                variant='destructive'
                                loading={deleting}
                                onClick={onConfirmDelete}>
                                {deleting ? null : <Trash2 />}
                                삭제
                            </Button>
                        </div>
                    </div>
                ) : null}
            </Modal>
        </section>
    )
}
